'use client';

/**
 * Wiki settings dialog
 *
 * Owns everything that configures how the wiki is produced: the two
 * automations (auto-refresh on a schedule, repo sync back into the
 * repository), the repo sync directory and the custom generation
 * instructions. Opened from the wiki sidebar header's overflow menu.
 */
import { c as _c } from "react/compiler-runtime";
import type { useLiveWikiSettings } from '@/data/wiki/use-wiki-settings';
import type { WikiSettingResponse, WikiSettingSchedule } from '@/lib/wiki-settings/schema';
import { HelpCircle, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { BetterTooltip } from '@/components/ui/tooltip';
import { WikiRepoSyncCardSection } from '@/components/wiki/wiki-repo-sync-card-section';
import { formatExactTimestamp } from '@/lib/utils/date-formatters';
import { getAllowedWikiAutoRefreshSchedules, getWikiAutoRefreshPlanTier, isWikiAutoRefreshEntitled, WIKI_AUTO_REFRESH_SCHEDULES } from '@/lib/wiki-settings/auto-refresh-plan';
import { CUSTOM_INSTRUCTIONS_MAX_LENGTH } from '@/lib/wiki-settings/constants';
import { DEFAULT_WIKI_REPO_SYNC_PATH } from '@/lib/wiki/repo-sync/path';
const SCHEDULE_LABELS: Record<WikiSettingSchedule, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly'
};
type WikiSettingsDialogProps = {
  /** Mounted only while open, so closing is a one-way callback. */
  onClose: () => void;
  installationId: number | null;
  repositoryId: number | null;
  settings: WikiSettingResponse | null | undefined;
  isLoading: boolean;
  isPersisting: boolean;
  updateSettings: ReturnType<typeof useLiveWikiSettings>['updateSettings'];
  /** First generation timestamp, shown as context for the private wiki slot. */
  firstGeneratedAt?: string | null;
  /** Days since the first generation, precomputed by the caller. */
  daysSinceGeneration?: number | null;
  onUpgradeRequired: () => void;
};
export function WikiSettingsDialog(t0) {
  const $ = _c(119);
  const {
    onClose,
    installationId,
    repositoryId,
    settings,
    isLoading,
    isPersisting,
    updateSettings,
    firstGeneratedAt,
    daysSinceGeneration,
    onUpgradeRequired
  } = t0;
  const [customInstructionsDraft, setCustomInstructionsDraft] = useState(null);
  const [repoSyncPathDraft, setRepoSyncPathDraft] = useState(null);
  const customInstructionsInput = customInstructionsDraft ?? settings?.customInstructions ?? "";
  const repoSyncPathInput = repoSyncPathDraft ?? settings?.repoSyncPath ?? DEFAULT_WIKI_REPO_SYNC_PATH;
  const autoRefreshEnabled = settings?.autoRefreshEnabled ?? false;
  const repoSyncAvailable = settings?.repoSyncAvailable ?? false;
  const repoSyncEnabled = repoSyncAvailable && (settings?.repoSyncEnabled ?? false);
  const autoRefreshSchedule = settings?.autoRefreshSchedule === "daily" || settings?.autoRefreshSchedule === "weekly" || settings?.autoRefreshSchedule === "monthly" ? settings.autoRefreshSchedule : "weekly";
  const hasActiveSubscription = settings?.hasActiveSubscription ?? false;
  const hasScanAccess = settings?.hasScanAccess ?? false;
  let t1;
  if ($[0] !== hasActiveSubscription || $[1] !== hasScanAccess) {
    t1 = isWikiAutoRefreshEntitled(hasScanAccess, hasActiveSubscription);
    $[0] = hasActiveSubscription;
    $[1] = hasScanAccess;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const hasAutoRefreshEntitlement = t1;
  let T0;
  let T1;
  let handleSaveSettings;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[3] !== autoRefreshEnabled || $[4] !== autoRefreshSchedule || $[5] !== customInstructionsInput || $[6] !== daysSinceGeneration || $[7] !== firstGeneratedAt || $[8] !== hasActiveSubscription || $[9] !== hasAutoRefreshEntitlement || $[10] !== hasScanAccess || $[11] !== installationId || $[12] !== isLoading || $[13] !== onClose || $[14] !== onUpgradeRequired || $[15] !== repoSyncEnabled || $[16] !== repoSyncPathInput || $[17] !== repositoryId || $[18] !== settings || $[19] !== updateSettings) {
    const autoRefreshPlanTier = getWikiAutoRefreshPlanTier(hasScanAccess, hasActiveSubscription);
    const allowedSchedules = getAllowedWikiAutoRefreshSchedules(autoRefreshPlanTier);
    const unavailableSchedules = WIKI_AUTO_REFRESH_SCHEDULES.filter(schedule => !allowedSchedules.includes(schedule));
    const selectedSchedule = allowedSchedules.includes(autoRefreshSchedule) ? autoRefreshSchedule : allowedSchedules[0] || "weekly";
    const showScanAccessUpgradeHint = hasActiveSubscription && !hasScanAccess;
    const handleToggle = checked => {
      if (checked && !hasAutoRefreshEntitlement) {
        onUpgradeRequired();
        return;
      }
      if (!installationId || !repositoryId) {
        return;
      }
      updateSettings(checked ? {
        autoRefreshEnabled: true,
        autoRefreshSchedule: selectedSchedule
      } : {
        autoRefreshEnabled: false
      });
    };
    let t11;
    if ($[32] !== installationId || $[33] !== repositoryId || $[34] !== updateSettings) {
      t11 = value => {
        if (!installationId || !repositoryId) {
          return;
        }
        updateSettings({
          autoRefreshSchedule: value
        });
      };
      $[32] = installationId;
      $[33] = repositoryId;
      $[34] = updateSettings;
      $[35] = t11;
    } else {
      t11 = $[35];
    }
    const handleScheduleChange = t11;
    let t12;
    if ($[36] !== customInstructionsInput || $[37] !== installationId || $[38] !== onClose || $[39] !== repoSyncEnabled || $[40] !== repoSyncPathInput || $[41] !== repositoryId || $[42] !== settings || $[43] !== updateSettings) {
      t12 = () => {
        if (!installationId || !repositoryId || !settings) {
          return;
        }
        const updatePayload = {
          customInstructions: customInstructionsInput.trim() || null,
          ...(repoSyncEnabled && {
            repoSyncPath: repoSyncPathInput.trim() || DEFAULT_WIKI_REPO_SYNC_PATH
          })
        };
        const transaction = updateSettings(updatePayload);
        transaction?.isPersisted.promise.then(() => {
          onClose();
        }).catch(_temp);
      };
      $[36] = customInstructionsInput;
      $[37] = installationId;
      $[38] = onClose;
      $[39] = repoSyncEnabled;
      $[40] = repoSyncPathInput;
      $[41] = repositoryId;
      $[42] = settings;
      $[43] = updateSettings;
      $[44] = t12;
    } else {
      t12 = $[44];
    }
    handleSaveSettings = t12;
    T1 = Dialog;
    t9 = true;
    if ($[45] !== onClose) {
      t10 = nextOpen => !nextOpen && onClose();
      $[45] = onClose;
      $[46] = t10;
    } else {
      t10 = $[46];
    }
    T0 = DialogContent;
    t6 = "sm:max-w-lg";
    if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <DialogHeader><DialogTitle>Wiki settings</DialogTitle><DialogDescription>Configure how your wiki is generated</DialogDescription></DialogHeader>;
      $[47] = t7;
    } else {
      t7 = $[47];
    }
    if ($[48] !== daysSinceGeneration || $[49] !== firstGeneratedAt) {
      t8 = firstGeneratedAt && daysSinceGeneration !== null && daysSinceGeneration !== undefined && <BetterTooltip content={formatExactTimestamp(firstGeneratedAt)}><p className="text-sm text-muted-foreground cursor-default">{`First generated ${daysSinceGeneration} ${daysSinceGeneration === 1 ? "day" : "days"} ago`}</p></BetterTooltip>;
      $[48] = daysSinceGeneration;
      $[49] = firstGeneratedAt;
      $[50] = t8;
    } else {
      t8 = $[50];
    }
    t5 = "space-y-4 py-4";
    t2 = "space-y-2";
    let t13;
    if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <h3 className="font-medium text-sm text-muted-foreground">Auto-refresh</h3>;
      $[51] = t13;
    } else {
      t13 = $[51];
    }
    const t14 = showScanAccessUpgradeHint ? "Regenerates the wiki on a schedule so it keeps up with code changes. Weekly and daily auto-refresh require Pro or Max; this plan supports monthly only." : "Regenerates the wiki on a schedule so it keeps up with code changes.";
    let t15;
    if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <HelpCircle className="h-3 w-3 text-muted-foreground/60 cursor-help" />;
      $[52] = t15;
    } else {
      t15 = $[52];
    }
    let t16;
    if ($[53] !== t14) {
      t16 = <div className="inline-flex items-center gap-1">{t13}<BetterTooltip content={t14}>{t15}</BetterTooltip></div>;
      $[53] = t14;
      $[54] = t16;
    } else {
      t16 = $[54];
    }
    let t17;
    if ($[55] !== autoRefreshEnabled || $[56] !== handleToggle || $[57] !== isLoading) {
      t17 = isLoading ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : <div className="flex items-center gap-2"><Switch aria-label="Auto-refresh" checked={autoRefreshEnabled} onCheckedChange={handleToggle} /></div>;
      $[55] = autoRefreshEnabled;
      $[56] = handleToggle;
      $[57] = isLoading;
      $[58] = t17;
    } else {
      t17 = $[58];
    }
    if ($[59] !== t16 || $[60] !== t17) {
      t3 = <div className="flex items-center justify-between">{t16}{t17}</div>;
      $[59] = t16;
      $[60] = t17;
      $[61] = t3;
    } else {
      t3 = $[61];
    }
    t4 = autoRefreshEnabled && <div className="space-y-2"><Select value={selectedSchedule} onValueChange={handleScheduleChange} disabled={isLoading}><SelectTrigger className="w-full h-8 text-xs bg-background/50"><SelectValue placeholder="Select schedule" /></SelectTrigger><SelectContent>{WIKI_AUTO_REFRESH_SCHEDULES.map(schedule_0 => {
            const isUnavailable = unavailableSchedules.includes(schedule_0);
            const isProOnly = isUnavailable && showScanAccessUpgradeHint && (schedule_0 === "daily" || schedule_0 === "weekly");
            const label = isProOnly ? `${SCHEDULE_LABELS[schedule_0]} (Pro)` : SCHEDULE_LABELS[schedule_0];
            return <SelectItem key={schedule_0} value={schedule_0} className="text-xs" disabled={isUnavailable}>{label}</SelectItem>;
          })}</SelectContent></Select></div>;
    $[3] = autoRefreshEnabled;
    $[4] = autoRefreshSchedule;
    $[5] = customInstructionsInput;
    $[6] = daysSinceGeneration;
    $[7] = firstGeneratedAt;
    $[8] = hasActiveSubscription;
    $[9] = hasAutoRefreshEntitlement;
    $[10] = hasScanAccess;
    $[11] = installationId;
    $[12] = isLoading;
    $[13] = onClose;
    $[14] = onUpgradeRequired;
    $[15] = repoSyncEnabled;
    $[16] = repoSyncPathInput;
    $[17] = repositoryId;
    $[18] = settings;
    $[19] = updateSettings;
    $[20] = T0;
    $[21] = T1;
    $[22] = handleSaveSettings;
    $[23] = t10;
    $[24] = t2;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
  } else {
    T0 = $[20];
    T1 = $[21];
    handleSaveSettings = $[22];
    t10 = $[23];
    t2 = $[24];
    t3 = $[25];
    t4 = $[26];
    t5 = $[27];
    t6 = $[28];
    t7 = $[29];
    t8 = $[30];
    t9 = $[31];
  }
  let t11;
  if ($[62] !== t2 || $[63] !== t3 || $[64] !== t4) {
    t11 = <div className={t2}>{t3}{t4}</div>;
    $[62] = t2;
    $[63] = t3;
    $[64] = t4;
    $[65] = t11;
  } else {
    t11 = $[65];
  }
  let t12;
  if ($[66] !== hasAutoRefreshEntitlement || $[67] !== installationId || $[68] !== isLoading || $[69] !== isPersisting || $[70] !== onUpgradeRequired || $[71] !== repositoryId || $[72] !== settings || $[73] !== updateSettings) {
    t12 = <WikiRepoSyncCardSection settings={settings} installationId={installationId} repositoryId={repositoryId} isLoading={isLoading} isPersisting={isPersisting} hasAutoRefreshEntitlement={hasAutoRefreshEntitlement} onUpgradeRequired={onUpgradeRequired} updateSettings={updateSettings} />;
    $[66] = hasAutoRefreshEntitlement;
    $[67] = installationId;
    $[68] = isLoading;
    $[69] = isPersisting;
    $[70] = onUpgradeRequired;
    $[71] = repositoryId;
    $[72] = settings;
    $[73] = updateSettings;
    $[74] = t12;
  } else {
    t12 = $[74];
  }
  let t13;
  if ($[75] !== isPersisting || $[76] !== repoSyncEnabled || $[77] !== repoSyncPathInput) {
    t13 = repoSyncEnabled && <div className="space-y-2"><Label htmlFor="repo-sync-path">Repo sync directory</Label><Input id="repo-sync-path" value={repoSyncPathInput} onChange={e => setRepoSyncPathDraft(e.target.value)} placeholder={DEFAULT_WIKI_REPO_SYNC_PATH} disabled={isPersisting} /><p className="text-xs text-muted-foreground">Wiki markdown is committed to this directory. Changing it queues a new sync PR.</p></div>;
    $[75] = isPersisting;
    $[76] = repoSyncEnabled;
    $[77] = repoSyncPathInput;
    $[78] = t13;
  } else {
    t13 = $[78];
  }
  let t14;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Label htmlFor="custom-instructions">Custom instructions</Label>;
    $[79] = t14;
  } else {
    t14 = $[79];
  }
  let t15;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = e_0 => setCustomInstructionsDraft(e_0.target.value);
    $[80] = t15;
  } else {
    t15 = $[80];
  }
  let t16;
  if ($[81] !== customInstructionsInput) {
    t16 = <Textarea id="custom-instructions" placeholder="e.g., Focus on API documentation, Write for beginners, Include code examples for each endpoint..." value={customInstructionsInput} onChange={t15} rows={5} maxLength={CUSTOM_INSTRUCTIONS_MAX_LENGTH} className="resize-none" />;
    $[81] = customInstructionsInput;
    $[82] = t16;
  } else {
    t16 = $[82];
  }
  let t17;
  if ($[83] !== customInstructionsInput.length) {
    t17 = customInstructionsInput.length > 0 && <span className="ml-1">{`(${customInstructionsInput.length}/${CUSTOM_INSTRUCTIONS_MAX_LENGTH})`}</span>;
    $[83] = customInstructionsInput.length;
    $[84] = t17;
  } else {
    t17 = $[84];
  }
  let t18;
  if ($[85] !== t17) {
    t18 = <p className="text-xs text-muted-foreground">Saved and used for future wiki generations.{t17}</p>;
    $[85] = t17;
    $[86] = t18;
  } else {
    t18 = $[86];
  }
  let t19;
  if ($[87] !== t16 || $[88] !== t18) {
    t19 = <div className="space-y-2">{t14}{t16}{t18}</div>;
    $[87] = t16;
    $[88] = t18;
    $[89] = t19;
  } else {
    t19 = $[89];
  }
  let t20;
  if ($[90] !== t11 || $[91] !== t12 || $[92] !== t13 || $[93] !== t19 || $[94] !== t5) {
    t20 = <div className={t5}>{t11}{t12}{t13}{t19}</div>;
    $[90] = t11;
    $[91] = t12;
    $[92] = t13;
    $[93] = t19;
    $[94] = t5;
    $[95] = t20;
  } else {
    t20 = $[95];
  }
  let t21;
  if ($[96] !== onClose) {
    t21 = <Button variant="outline" onClick={onClose}>Cancel</Button>;
    $[96] = onClose;
    $[97] = t21;
  } else {
    t21 = $[97];
  }
  const t22 = isPersisting || isLoading || !settings;
  let t23;
  if ($[98] !== isPersisting) {
    t23 = isPersisting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null;
    $[98] = isPersisting;
    $[99] = t23;
  } else {
    t23 = $[99];
  }
  let t24;
  if ($[100] !== handleSaveSettings || $[101] !== t22 || $[102] !== t23) {
    t24 = <Button onClick={handleSaveSettings} disabled={t22}>{t23}Save</Button>;
    $[100] = handleSaveSettings;
    $[101] = t22;
    $[102] = t23;
    $[103] = t24;
  } else {
    t24 = $[103];
  }
  let t25;
  if ($[104] !== t21 || $[105] !== t24) {
    t25 = <DialogFooter>{t21}{t24}</DialogFooter>;
    $[104] = t21;
    $[105] = t24;
    $[106] = t25;
  } else {
    t25 = $[106];
  }
  let t26;
  if ($[107] !== T0 || $[108] !== t20 || $[109] !== t25 || $[110] !== t6 || $[111] !== t7 || $[112] !== t8) {
    t26 = <T0 className={t6}>{t7}{t8}{t20}{t25}</T0>;
    $[107] = T0;
    $[108] = t20;
    $[109] = t25;
    $[110] = t6;
    $[111] = t7;
    $[112] = t8;
    $[113] = t26;
  } else {
    t26 = $[113];
  }
  let t27;
  if ($[114] !== T1 || $[115] !== t10 || $[116] !== t26 || $[117] !== t9) {
    t27 = <T1 open={t9} onOpenChange={t10}>{t26}</T1>;
    $[114] = T1;
    $[115] = t10;
    $[116] = t26;
    $[117] = t9;
    $[118] = t27;
  } else {
    t27 = $[118];
  }
  return t27;
}
function _temp() {}